import { useEffect, useState } from 'react';

import { useGetApiV1PersonIdPhoto } from '@/shared/api/generated/hooks/personRegisterController/useGetApiV1PersonIdPhoto';

/**
 * Busca a foto de perfil de uma pessoa e converte o blob em uma object URL utilizável.
 * Retorna `undefined` quando o id não está disponível ou a pessoa não possui foto.
 *
 * @param id - ID da pessoa. `undefined` desabilita a requisição.
 * @returns Object URL da foto ou `undefined`.
 *
 * @example
 * ```tsx
 * const photoUrl = usePersonPhoto(person?.id);
 * <Avatar src={photoUrl} fallback={initials} />
 * ```
 */
export function usePersonPhoto(id: number | undefined): string | undefined {
  const [photoUrl, setPhotoUrl] = useState<string | undefined>(undefined);

  const { data } = useGetApiV1PersonIdPhoto(id ?? 0, {
    query: {
      enabled: !!id,
      retry: false,
    },
    client: { responseType: 'blob' },
  });

  useEffect(() => {
    if (!data) {
      setPhotoUrl(undefined);
      return;
    }

    const url = URL.createObjectURL(data as Blob);
    setPhotoUrl(url);

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [data]);

  return photoUrl;
}
